/**
 * Canonical display order for inventory item types, by category.
 *
 * Used wherever the app presents assets or liabilities grouped by type and needs a
 * predictable, scannable order — the Add asset / liability type picker and the Grant
 * source cheatsheet. Keeping the order in one place stops the two surfaces drifting apart.
 *
 * A slug not listed here sorts after all known slugs (see {@link compareByTypeOrder}).
 */
export const ASSET_TYPE_ORDER: readonly string[] = [
  'real-estate',
  'bank-account',
  'nursing-home-bond',
  'employment-entitlements',
  'superannuation',
  'srn-shares',
  'hin-shares',
  'life-insurance',
  'cryptocurrency',
  'vehicle',
  'managed-funds',
  'private-company',
  'other'
];

export const LIABILITY_TYPE_ORDER: readonly string[] = [
  'funeral-burial',
  'legal-professional',
  'mortgage',
  'vehicle-loan',
  'tax-liability',
  'credit-card',
  'personal-loan',
  'utility-bill',
  'estate-expense',
  'other-liability'
];

/**
 * Orders two type slugs by their position in `order`. Slugs in the list sort by
 * position; a slug absent from the list sorts after any listed slug, and two absent
 * slugs compare equal (0) so the caller can keep their original relative order or apply
 * its own tie-break.
 */
export function compareByTypeOrder(
  aSlug: string,
  bSlug: string,
  order: readonly string[]
): number {
  const aIndex = order.indexOf(aSlug);
  const bIndex = order.indexOf(bSlug);
  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
  if (aIndex !== -1) return -1;
  if (bIndex !== -1) return 1;
  return 0;
}

/**
 * A group of inventory item types shown as one collapsible section in the Add
 * asset / liability picker (VALE-1624).
 *
 * `slugs` declares membership only. Within-group display order comes from the
 * canonical {@link ASSET_TYPE_ORDER} / {@link LIABILITY_TYPE_ORDER} via
 * {@link compareByTypeOrder}, so there is a single ordering source and the group
 * definitions never have to be kept in step with it.
 */
export interface InventoryTypeGroupDef {
  /** Stable identity: React key and the CollapsibleSection `sectionKey`. */
  key: string;
  /** Heading, in natural case (the section header renders it uppercase itself). */
  label: string;
  /** Type slugs that belong to this group. */
  slugs: readonly string[];
}

/**
 * Asset groups, in section order. Mirrors the approved redesign mock. Every slug
 * here is an {@link ASSET_TYPE_ORDER} member; the final group is the catch-all a
 * slug in no other group falls into (see {@link groupInventoryTypesByCategory}).
 */
export const ASSET_TYPE_GROUPS: readonly InventoryTypeGroupDef[] = [
  { key: 'property', label: 'Property', slugs: ['real-estate', 'vehicle'] },
  { key: 'cash-banking', label: 'Cash & banking', slugs: ['bank-account'] },
  {
    key: 'investments',
    label: 'Investments',
    slugs: [
      'srn-shares',
      'hin-shares',
      'managed-funds',
      'private-company',
      'cryptocurrency'
    ]
  },
  {
    key: 'super-insurance',
    label: 'Super & insurance',
    slugs: ['superannuation', 'life-insurance']
  },
  {
    key: 'owed-to-estate',
    label: 'Amounts owed to the estate',
    slugs: ['nursing-home-bond', 'employment-entitlements']
  },
  { key: 'other', label: 'Other', slugs: ['other'] }
];

/**
 * Liability groups, in section order. Mirrors the approved redesign mock: the
 * deceased's own debts (Loans, Credit & bills, Tax) are separated from the costs
 * of administering the estate.
 */
export const LIABILITY_TYPE_GROUPS: readonly InventoryTypeGroupDef[] = [
  {
    key: 'loans',
    label: 'Loans',
    slugs: ['mortgage', 'vehicle-loan', 'personal-loan']
  },
  {
    key: 'credit-bills',
    label: 'Credit & bills',
    slugs: ['credit-card', 'utility-bill']
  },
  { key: 'tax', label: 'Tax', slugs: ['tax-liability'] },
  {
    key: 'estate-admin-costs',
    label: 'Estate administration costs',
    slugs: ['funeral-burial', 'legal-professional', 'estate-expense']
  },
  { key: 'other', label: 'Other', slugs: ['other-liability'] }
];

/**
 * One-line notes shown inline after each type name in the picker (VALE-1624).
 *
 * The backend list endpoint returns only `{name, slug, iconUrl}`, so there is no
 * server-side description to bind to; these are the single source for that copy.
 * A backend-provided `InventoryItemType.description` still wins when present (see
 * the picker), leaving this as the fallback. Customer-facing prose: keep it plain
 * and reassuring, and never use an em dash.
 */
export const INVENTORY_TYPE_DESCRIPTIONS: Readonly<Record<string, string>> = {
  // Assets
  'real-estate': 'Homes, land and other property',
  'bank-account': 'Everyday, savings and term deposits',
  'nursing-home-bond': 'Refundable accommodation deposit',
  superannuation: 'Balance and any death benefit',
  'srn-shares': 'Held with the issuer (SRN)',
  'hin-shares': 'Held via a broker (CHESS, HIN)',
  'life-insurance': 'Pays a benefit on death',
  cryptocurrency: 'Wallet or exchange holdings',
  vehicle: 'Cars, boats and caravans',
  'managed-funds': 'Pooled investments and unit trusts',
  'private-company': 'Shares in a private company',
  'employment-entitlements': 'Unpaid wages, leave and final pay',
  other: "Doesn't fit the categories above",
  // Liabilities
  mortgage: 'Loan secured against real estate',
  'vehicle-loan': 'Loan secured against a vehicle',
  'personal-loan': 'Unsecured loans and credit lines',
  'credit-card': 'Outstanding card balances',
  'utility-bill': 'Electricity, gas, water and similar',
  'tax-liability': 'Tax owed by the estate',
  'funeral-burial': 'Funeral, burial and cremation costs',
  'legal-professional': 'Solicitor, accountant and agent fees',
  'estate-expense': 'Costs of administering the estate',
  'other-liability': "Doesn't fit the categories above"
};

/** One resolved group: its heading and the types that fall under it, ordered. */
export interface GroupedInventoryTypes<T> {
  key: string;
  label: string;
  types: T[];
}

/**
 * Bucket the fetched inventory types into their display groups for a category.
 *
 * Membership comes from {@link ASSET_TYPE_GROUPS} / {@link LIABILITY_TYPE_GROUPS};
 * a type whose slug is in no group falls into the final ("Other") group so nothing
 * a future backend adds silently disappears. Within each group, types keep the
 * canonical order. Empty groups are dropped, so a category that returns no members
 * for a group renders no empty section.
 */
export function groupInventoryTypesByCategory<T extends { slug: string }>(
  types: readonly T[],
  category: 'asset' | 'liability' | undefined
): GroupedInventoryTypes<T>[] {
  const groups =
    category === 'liability' ? LIABILITY_TYPE_GROUPS : ASSET_TYPE_GROUPS;
  const order =
    category === 'liability' ? LIABILITY_TYPE_ORDER : ASSET_TYPE_ORDER;

  const slugToGroupKey = new Map<string, string>();
  for (const group of groups) {
    for (const slug of group.slugs) slugToGroupKey.set(slug, group.key);
  }
  const fallbackKey = groups[groups.length - 1]?.key;

  const buckets = new Map<string, T[]>(groups.map((group) => [group.key, []]));
  for (const type of types) {
    if (!type?.slug) continue;
    const key = slugToGroupKey.get(type.slug) ?? fallbackKey;
    if (key) buckets.get(key)?.push(type);
  }

  return groups
    .map((group) => ({
      key: group.key,
      label: group.label,
      types: [...(buckets.get(group.key) ?? [])].sort((a, b) =>
        compareByTypeOrder(a.slug, b.slug, order)
      )
    }))
    .filter((group) => group.types.length > 0);
}
